import React from 'react';
import { Calendar } from 'lucide-react';

const BRAND_BROWN = '#8B4513';

export default function TopBar({ title, breadcrumb, children }) {
  return (
    <div style={{
      height: '80px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 40px',
      position: 'sticky',
      top: 0,
      zIndex: 50,
      overflow: 'hidden'
    }}>
      {/* Blurred background image with parallax effect */}
      <div style={{
        position: 'absolute',
        top: '-10px',
        left: '-10px',
        right: '-10px',
        bottom: '-10px',
        backgroundImage: 'url(/brand/shop-bg.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        filter: 'blur(4px)',
        transform: 'scale(1.1)',
        zIndex: 1
      }} />

      {/* Gradient overlay for depth */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(139, 69, 19, 0.4) 100%)',
        zIndex: 2
      }} />

      {/* Left: Title & Breadcrumb with icon */}
      <div style={{ 
        position: 'relative', 
        zIndex: 4,
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        {/* Decorative icon */}
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
        }}>
          <Calendar size={24} strokeWidth={2} />
        </div>
        
        <div>
          <h1 style={{
            margin: 0,
            fontSize: '26px',
            fontWeight: '700',
            color: 'white',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
            letterSpacing: '-0.5px'
          }}>
            {title}
          </h1>
          {breadcrumb && (
            <div style={{
              fontSize: '13px',
              color: 'rgba(255, 255, 255, 0.9)',
              marginTop: '4px',
              textShadow: '0 1px 4px rgba(0, 0, 0, 0.5)',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              {breadcrumb.split(' › ').map((part, i, arr) => (
                <React.Fragment key={i}>
                  <span style={{
                    opacity: i === arr.length - 1 ? 1 : 0.7
                  }}>
                    {part}
                  </span>
                  {i < arr.length - 1 && (
                    <span style={{ opacity: 0.5 }}>›</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right: Action buttons with enhanced glassy effect */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        position: 'relative',
        zIndex: 4
      }}>
        {React.Children.map(children, child => {
          if (!child) return null;
          
          // Apply enhanced glassy effect to all children
          return React.cloneElement(child, {
            style: {
              ...child.props.style,
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              fontWeight: '600',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            },
            onMouseEnter: (e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
              child.props.onMouseEnter?.(e);
            },
            onMouseLeave: (e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
              child.props.onMouseLeave?.(e);
            }
          });
        })}
      </div>
    </div>
  );
}